import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {addMonths} from 'date-fns/addMonths';
import {startOfMonth} from 'date-fns/startOfMonth';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  NewSelectField,
  showModal,
} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {FieldsRow, Text} from 'src/components/primitives';
import {CHANNEL_LABEL, EChannelType} from 'src/constants';
import {Banner} from 'src/containers/Banner';
import {ELicensePeriod} from 'src/containers/Licenses/definitions';
import {joiResolver, parseISO} from 'src/utils';

import {
  useChangeConnectedTariffMutation,
  useGetAvailableTariffsQuery,
  useGetCatalogTariffLicenseQuery,
  useGetTariffLicensesByIdsQuery,
} from '../api';
import {DETAIL_PERIOD_MONTHS, TAvailableTariff, TConnectedTariff} from '../definitions';
import {useLicenseInfo} from '../useLicenseInfo';
import {COLUMNS_WIDTH, WHATSAPP_COLUMNS_WIDTH} from '../utils';
import {showCreateTariffModal} from './CreateTariffModal';
import * as S from './style';

type TFormData = {licenseDetailId: number};

const EditConnectedTariffModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TAvailableTariff;
  connectedTariff: TConnectedTariff;
}>(({closeModal, tariff, connectedTariff}) => {
  const {t} = useTranslation();
  const {id: tenantId} = useParams();

  const [reselectAvailableTariff, {isLoading}] = useChangeConnectedTariffMutation();
  const {data: license} = useGetCatalogTariffLicenseQuery({
    tariffId: tariff.tariffCatalogId,
    licenseId: tariff.licenseDetailId,
  });

  const isWhatsApp = tariff.channelType === EChannelType.WHATSAPP;
  const licensePeriod =
    license?.data?.periodMonths === DETAIL_PERIOD_MONTHS[ELicensePeriod.YEAR]
      ? ELicensePeriod.YEAR
      : ELicensePeriod.MONTH;

  const {data: tariffs = []} = useGetAvailableTariffsQuery(
    {tenantId, channelType: tariff.channelType},
    {skip: !license || !tenantId},
  );

  const availableLicenses = React.useMemo(
    () =>
      tariffs
        .filter(
          (item) =>
            item.active.licenseDetailId !== tariff.licenseDetailId ||
            item.active.tariffCatalogId !== tariff.tariffCatalogId,
        )
        .reduce<
          Record<number, string>
        >((result, item) => ({...result, [item.active.licenseDetailId]: item.active.name}), {}),
    [tariffs, tariff],
  );
  const availableLicensesIds = React.useMemo(
    () => Object.keys(availableLicenses),
    [availableLicenses],
  );

  const {data: licenses = []} = useGetTariffLicensesByIdsQuery(availableLicensesIds, {
    skip: !availableLicensesIds.length,
  });

  const licensesOptions = React.useMemo(
    () =>
      licenses
        .filter((item) => item.data?.periodMonths === DETAIL_PERIOD_MONTHS[licensePeriod])
        .map((item) => ({
          label: availableLicenses[item.id] || item.label,
          value: item.id,
        })),
    [licenses, licensePeriod],
  );

  const licenseInfo = useLicenseInfo({
    tariffCatalogId: tariff.tariffCatalogId,
    licenseDetailId: tariff.licenseDetailId,
  });
  const currency = licenseInfo.currency;

  const methods = useForm<TFormData>({
    mode: 'all',
    resolver: joiResolver((joi) => joi.object({licenseDetailId: joi.number().required()})),
  });

  const selectedLicenseDetailId = methods.watch('licenseDetailId');

  const handleSubmit = async ({licenseDetailId}: TFormData) => {
    try {
      const newTariff = tariffs.find(
        (item) => item.active.licenseDetailId === licenseDetailId,
      )?.active;
      const newLicense = licenses.find((item) => item.id === licenseDetailId);

      if (!newTariff || !newLicense) {
        return;
      }

      await reselectAvailableTariff({
        newAvlTariffId: newTariff.id,
        id: connectedTariff.id,
        version: connectedTariff.version,
        name: tariff.name,
      }).unwrap();

      closeModal();
    } catch (e) {}
  };

  const isOnlyTargetTariff = React.useMemo(
    () => tariffs.length === 1 && tariffs[0].active.id === tariff.id,
    [tariffs, tariff],
  );

  return (
    <Modal width="830px">
      <FormProvider {...methods}>
        <ModalHeader>
          {t('Tariffs:editConnectedTariffModal.title', {
            channelType: CHANNEL_LABEL[tariff.channelType],
          })}
        </ModalHeader>
        <Stack size="4" isFullWidth>
          <Text>{t('Tariffs:editConnectedTariffModal.parameters')}</Text>
          {isWhatsApp && (
            <Stack isFullWidth size="2">
              <S.Label>{t('Tariffs:editConnectedTariffModal.period')}</S.Label>
              <Stack direction="ROW" isFullWidth size="6">
                <S.Radio type="radio" checked={licensePeriod === ELicensePeriod.MONTH} disabled>
                  {t(`Tariffs:licensePeriod.${ELicensePeriod.MONTH}`)}
                </S.Radio>
                <S.Radio type="radio" checked={licensePeriod === ELicensePeriod.YEAR} disabled>
                  {t(`Tariffs:licensePeriod.${ELicensePeriod.YEAR}`)}
                </S.Radio>
              </Stack>
            </Stack>
          )}
          <Stack size="2" isFullWidth>
            {isWhatsApp && (
              <FieldsRow size="5" columnsWidth={WHATSAPP_COLUMNS_WIDTH}>
                <S.Label>{t('Tariffs:createTariffModal.mauCount')}</S.Label>
                <S.NumberInput value={licenseInfo.mauCount ?? 0} readOnly />
                <S.Label>{t('Tariffs:createTariffModal.licensePrice', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.licensePrice} readOnly />
                <S.Label>{t('Tariffs:createTariffModal.connectionFee', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.connectionFee} readOnly />
                <S.Label>{t('Tariffs:createTariffModal.amauPrice', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.amauPrice} readOnly />
              </FieldsRow>
            )}
            {!isWhatsApp && (
              <FieldsRow size="5" columnsWidth={COLUMNS_WIDTH}>
                <S.Label>{t('Tariffs:createTariffModal.monthLicensePrice', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.licensePrice} readOnly />
              </FieldsRow>
            )}
            <Text secondary size="XS">
              {t('Tariffs:pricesIsWithoutVat')}
            </Text>
          </Stack>
          <Text>{t('Tariffs:editConnectedTariffModal.nextTariff')}</Text>
          {isOnlyTargetTariff && (
            <Banner type="INFO" fontSize="S" shadowSize="XS" noMargin>
              {t('Tariffs:editConnectedTariffModal.onlyTargetTariffHint')}
            </Banner>
          )}
          {!!selectedLicenseDetailId && (
            <Banner type="INFO" fontSize="S" shadowSize="XS" noMargin>
              {t('Tariffs:editConnectedTariffModal.willChangeAt', {
                date: parseISO(startOfMonth(addMonths(new Date(), 1)), 'DATE'),
              })}
            </Banner>
          )}
          <NewSelectField
            label={t('Tariffs:editConnectedTariffModal.selectNextTariff')}
            disabled={isOnlyTargetTariff}
            name="licenseDetailId"
            noMargin
            options={licensesOptions}
          />
        </Stack>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          {!isOnlyTargetTariff && (
            <ModalSubmitButton
              disabled={isLoading}
              loading={isLoading}
              onClick={methods.handleSubmit(handleSubmit)}
            >
              {t('Tariffs:editConnectedTariffModal.planButton')}
            </ModalSubmitButton>
          )}
          {isOnlyTargetTariff && (
            <ModalSubmitButton
              onClick={() => {
                closeModal();
                showCreateTariffModal({channelType: tariff.channelType, licensePeriod});
              }}
            >
              {t('Tariffs:editConnectedTariffModal.createTariffButton')}
            </ModalSubmitButton>
          )}
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

EditConnectedTariffModal.displayName = 'EditConnectedTariffModal';

export const showEditConnectedTariffModal = (
  tariff: TAvailableTariff,
  connectedTariff: TConnectedTariff,
) =>
  showModal((closeModal) => (
    <EditConnectedTariffModal
      closeModal={closeModal}
      tariff={tariff}
      connectedTariff={connectedTariff}
    />
  ));
